// --- Profile CC list util. ---

import { creditCardType } from '../const'
import type { CreditCardInfo } from '../../../types'

// - to render the profile list of credit cards merged from two sources
// - to delete from those two sources

// Source 1, also used as the displayable card type
type CC1 = { number: string, expirationDate: string, ...any }

type CC2 = { expiryMonth: string, expiryYear: string, lastFour: string, ...any }

export const creditCardToYMF = ({
    number,
    expirationDate
}: CC1): string | null => {
    if (!number || !expirationDate) return null
    const m1 = number.match(/\d{4}$/) // CCCC
    const m2 = expirationDate.match(/^(\d\d)\/(\d\d)$/) // MM/YY
    return m1 && m2 ? `${m2[2]}${m2[1]}${m1[0]}` : null // YYMMCCCC
}

const creditCardV2ToYMF = ({
    expiryMonth,
    expiryYear,
    lastFour
}: CC2): string | null => {
    if (!expiryMonth || !expiryYear || !lastFour) return null
    return expiryMonth.match(/^\d\d$/) && // MM
    expiryYear.match(/^\d\d$/) && // YY
        lastFour.match(/^\d{4}$/) // CCCC
        ? `${expiryYear}${expiryMonth}${lastFour}`
        : null // YYMMCCCC
}

export const cardsMatchingYMF = (
    creditCards: CC1[],
    creditCardsV2: CC2[],
    cardYMF: string | null
): [CC1[], CC2[]] => [
    creditCards.filter(cc => creditCardToYMF(cc) === cardYMF),
    creditCardsV2.filter(cc => creditCardV2ToYMF(cc) === cardYMF)
]

const cardsToUniqSortedYMF = (
    creditCards: CC1[],
    creditCardsV2: CC2[]
): string[] => {
    const mix = [
        ...creditCards.map(creditCardToYMF).filter(Boolean),
        ...creditCardsV2.map(creditCardV2ToYMF).filter(Boolean)
    ]
        .sort()
        .reverse()
    const result = []
    mix.forEach(cc => {
        if (!result.includes(cc)) result.push(cc)
    })
    return result
}

const displayableCardFromYMF = (cardYMF: string | null): CC1 | null => {
    const m = cardYMF ? cardYMF.match(/^(\d\d)(\d\d)(\d{4})$/) : null
    return m
        ? { number: `xxxxxxxx${m[3]}`, expirationDate: `${m[2]}/${m[1]}` }
        : null
}

export const getDisplayableCardList = (
    creditCards: CC1[],
    creditCardsV2: CC2[]
) =>
    cardsToUniqSortedYMF(creditCards, creditCardsV2)
        .map(displayableCardFromYMF)
        .filter(Boolean)

export const deleteFirstCardOfEachSource = (
    creditCardYMFToDelete: string,
    creditCards: CC1[],
    creditCardsV2: CC2[],
    deleteCreditCard: (id: string) => void,
    deleteCreditCardV2: (id: string) => void
) => {
    if (creditCardYMFToDelete) {
        const [matches, matches2] = cardsMatchingYMF(
            creditCards,
            creditCardsV2,
            creditCardYMFToDelete
        )
        if (matches.length) {
            deleteCreditCard(matches[0].id)
        }
        if (matches2.length) {
            deleteCreditCardV2(matches2[0].id)
        }
    }
}

export const useStoredCard = ({
    profileCreditCards,
    creditCardsToFilter,
    isAdyenRestaurant,
    authenticated,
    isDelivery
}: {
    profileCreditCards: any,
    creditCardsToFilter: any,
    isAdyenRestaurant: boolean,
    authenticated: boolean,
    isDelivery: boolean
}) => {
    let cardIdSelected = creditCardType.CB

    // User not authenticated or has no stored card
    if (
        !authenticated ||
        !creditCardsToFilter ||
        creditCardsToFilter.length === 0
    ) {
        return {
            storedCards: [],
            cardIdSelected
        }
    }

    const activeCreditCards = profileCreditCards.map(
        creditCard => creditCard.number || creditCard.lastFour
    )
    const usableStoredCards = creditCardsToFilter.filter(creditCard =>
        activeCreditCards.includes(creditCard.number || creditCard.lastFour)
    )

    if (isAdyenRestaurant) {
        const storedCard = usableStoredCards?.[0]

        cardIdSelected = storedCard?.id || creditCardType.CB
    } else {
        cardIdSelected =
            (creditCardsToFilter &&
                creditCardsToFilter.length > 0 &&
                (
                    (!isDelivery && creditCardsToFilter[0]) ||
                    creditCardsToFilter.find(
                        creditCard => !creditCard.lunchVoucher
                    )
                )?.id) ||
            creditCardType.CB
    }

    return {
        storedCards: usableStoredCards,
        cardIdSelected
    }
}
